import React, { useState, useCallback, useMemo, useContext } from 'react';
import {
  View,
  Pressable,
  Platform,
  ScrollView,
} from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Text } from '@constants/Themed';
import { useDispatch } from 'react-redux';
import { MaterialIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { YStack, H6, Spinner } from 'tamagui';
import { BillingInfo } from '../types/types';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { SHOW_AUTH_MODAL, SHOW_LOADING_MODAL, HIDE_LOADING_MODAL } from '@reduxLocal/actions/types';
import apis from '@api/api';
import { selectUser } from '@reduxLocal/selectors/userSelectors';
import * as Stripe from '@stripe/stripe-react-native';
import { IAPContext } from '@context/IAPContext';
import styles from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import {
  useHasActiveSubscriptionWithContext,
  useIsFreePlanWithContext,
  useAIUsageStatsForFreePlan,
  useCompanyNumber
} from '@utils/billingUtils';

interface StripeProduct {
  id: string;
  name: string;
  description: string;
  active: boolean;
  default_price: {
    id: string;
    unit_amount: number | null;
    currency: string;
    type: 'recurring' | 'one_time';
  };
  metadata: Record<string, string>;
  images: string[];
}

interface AICreditsComponentProps {
  isNative?: boolean;
  activeTab?: 'iap' | 'stripe';
}

const AICreditsComponent: React.FC<AICreditsComponentProps> = ({
  isNative = false,
  activeTab = 'stripe'
}) => {
  const dispatch = useDispatch();
  const theme = themeSelector();
  const queryClient = useQueryClient();
  const user = selectUser();
  const [selectedCredit, setSelectedCredit] = useState<StripeProduct | null>(null);
  const [paymentInProgress, setPaymentInProgress] = useState(false);

  // Use billing utilities hooks
  const companyNumber = useCompanyNumber();
  const hasActiveSubscription = useHasActiveSubscriptionWithContext();
  const isFreePlan = useIsFreePlanWithContext();
  const { data: aiUsageStats, isLoading: aiUsageLoading } = useAIUsageStatsForFreePlan(companyNumber);

  // Get current plan for display purposes
  const currentPlan: BillingInfo | undefined = queryClient.getQueryData(
    companyNumber ? ['subscriptionPlan', companyNumber] : ['subscriptionPlan']
  );

  // Check if product is EXEMPT_PRODUCT
  const isExemptProduct = useMemo(() => {
    if (!currentPlan) return false;

    const productName =
      (currentPlan as any)?.product ||
      currentPlan.product?.name ||
      currentPlan.productName ||
      currentPlan.product?.id;

    return productName === 'EXEMPT_PRODUCT';
  }, [currentPlan]);

  // Stripe hooks (only for native platforms)
  const { initPaymentSheet, presentPaymentSheet } =
    Platform.OS !== 'web' ? Stripe.useStripe() : { initPaymentSheet: null, presentPaymentSheet: null };

  // IAP context - use useContext directly (returns null if not in provider)
  // Only use when on native and IAP tab is active
  const iapContextValue = useContext(IAPContext);
  const iapContext = (isNative && activeTab === 'iap' && iapContextValue) ? iapContextValue : null;

  // Fetch credit products - only one-time purchases
  const shouldFetchCredits = !isNative || activeTab === 'stripe';

  const { data: planData, isLoading: creditsLoading } = useQuery({
    queryKey: ['creditProducts', 'stripe'],
    queryFn: async () => {
      const res = await apis.getProductsInternal();
      return res;
    },
    enabled: shouldFetchCredits,
    staleTime: 5 * 60 * 1000,
  });

  // AI Credits Product ID for dev environment
  const AI_CREDITS_PRODUCT_ID = 'prod_TaYdkjkwRiIxEl';

  // Filter for one-time credit products
  const creditProducts = useMemo(() => {
    if (!planData) return [];

    let products: any[] = [];

    // Handle different response structures
    if (Array.isArray(planData)) {
      products = planData;
    } else if (planData?.success && Array.isArray(planData?.data)) {
      products = planData.data;
    } else if (planData?.success && planData?.data?.data && Array.isArray(planData.data.data)) {
      products = planData.data.data;
    }

    // Filter for one-time credit products
    // Specifically include the AI credits product ID, or filter by metadata/name
    return products.filter((item) => {
      // Always include the specific AI credits product ID
      const isAICreditsProduct = item.id === AI_CREDITS_PRODUCT_ID;

      // Also include products that match credit criteria
      const isOneTime = item.default_price?.type === 'one_time';
      const isCredit = item.metadata?.type === 'ai_credits' ||
        item.name?.toLowerCase().includes('credit') ||
        item.metadata?.product_type === 'credits' ||
        item.metadata?.product_id === AI_CREDITS_PRODUCT_ID;
      const isActive = item.active !== false;

      // Include if it's the specific product ID OR matches credit criteria
      return (isAICreditsProduct || (isOneTime && isCredit)) && isActive;
    }).sort((a, b) => {
      const amountA = a.default_price?.unit_amount || 0;
      const amountB = b.default_price?.unit_amount || 0;
      return amountA - amountB;
    });
  }, [planData]);

  // IAP products (for native IAP tab)
  const iapCreditProducts = useMemo(() => {
    if (!iapContext?.products || activeTab !== 'iap') return [];

    // Filter IAP products for credits (assuming they have 'credit' in the product ID or name)
    return iapContext.products.filter((product: any) => {
      const productId = product.id?.toLowerCase() || '';
      const productTitle = product.title?.toLowerCase() || '';
      return productId.includes('credit') || productTitle.includes('credit');
    }).map((product: any) => ({
      id: product.id,
      name: product.title || product.id,
      description: product.description || '',
      price: product.price,
      localizedPrice: product.displayPrice || product.price,
      currency: product.currency,
      productId: product.id,
    }));
  }, [iapContext?.products, activeTab]);

  const formatPrice = (amount: number | null, currency: string) => {
    if (amount === null || amount === undefined) {
      return 'Contact Sales';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  // Handle Stripe one-time purchase
  const handleStripePurchase = useCallback(async (product: StripeProduct) => {
    if (!product.default_price?.id) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Price ID not found for this product', type: 'error' },
      });
      return;
    }

    if (!product.id) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Product ID not found', type: 'error' },
      });
      return;
    }

    try {
      setPaymentInProgress(true);
      dispatch({ type: SHOW_LOADING_MODAL });

      console.log('🛒 [AI Credits] Starting purchase flow:', {
        productId: product.id,
        productName: product.name,
        priceId: product.default_price.id,
        priceAmount: product.default_price.unit_amount,
        platform: Platform.OS,
      });

      if (Platform.OS === 'web') {
        // Web: Use checkout session for one-time payment
        // Note: The backend endpoint expects a price ID (not product ID) for checkout sessions
        console.log('🌐 [AI Credits] Creating checkout session for web...');
        const checkoutSession = await apis.getCheckoutSession({
          productId: product.default_price.id, // Pass price ID, not product ID
        });

        console.log('✅ [AI Credits] Checkout session created:', {
          hasUrl: !!checkoutSession?.url,
          sessionId: checkoutSession?.id,
        });

        if (checkoutSession?.url) {
          // Redirect to Stripe checkout
          window.location.href = checkoutSession.url;
        } else if (checkoutSession?.clientSecret) {
          // If using embedded checkout, handle differently
          throw new Error('Embedded checkout not yet implemented for credits');
        } else {
          throw new Error('Failed to create checkout session - no URL or client secret returned');
        }
      } else {
        // Native: Use payment sheet for one-time payment
        const billingId = user?.billing_info?.stripe?.billingId;

        if (!billingId) {
          throw new Error('No billing ID found. Please ensure you have a payment method set up.');
        }

        console.log('📱 [AI Credits] Creating payment sheet for native...', {
          productId: product.id,
          billingId,
        });

        // Use paymentSheet API for one-time purchases
        // Pass product ID (not price ID) - backend will retrieve the price
        const paymentSheetResponse = await apis.paymentSheet({
          product: product.id,
          billingId: billingId,
        });

        // The API returns { data: { paymentIntent, ephemeralKey, customer, publishableKey } }
        const responseData = paymentSheetResponse?.data || paymentSheetResponse;

        console.log('✅ [AI Credits] Payment sheet response received:', {
          hasPaymentIntent: !!responseData?.paymentIntent,
          hasEphemeralKey: !!responseData?.ephemeralKey,
          hasCustomer: !!responseData?.customer,
        });

        if (!responseData) {
          throw new Error('Failed to create payment sheet - no response from server');
        }

        const { paymentIntent, ephemeralKey, customer } = responseData;

        if (!paymentIntent || !ephemeralKey || !customer) {
          throw new Error('Payment sheet response missing required fields');
        }

        // Initialize payment sheet
        const customerId = typeof customer === 'object' ? customer.id : customer;
        const { error: initError } = await initPaymentSheet({
          merchantDisplayName: 'SmartSaaS',
          paymentIntentClientSecret: paymentIntent,
          customerEphemeralKeySecret: ephemeralKey,
          customerId: customerId,
          defaultBillingDetails: {
            name: user?.name || '',
            email: user?.email || '',
          },
          allowsDelayedPaymentMethods: false,
        });

        if (initError) {
          console.error('❌ [AI Credits] Payment sheet initialization error:', initError);
          throw new Error(initError.message || 'Failed to initialize payment sheet');
        }

        console.log('✅ [AI Credits] Payment sheet initialized, presenting...');

        // Hide loading modal so user can interact with payment sheet
        dispatch({ type: HIDE_LOADING_MODAL });

        // Present payment sheet
        const { error: presentError } = await presentPaymentSheet();

        if (presentError) {
          console.error('❌ [AI Credits] Payment sheet presentation error:', presentError);
          throw new Error(presentError.message || 'Payment was cancelled or failed');
        }

        console.log('✅ [AI Credits] Payment completed successfully');

        // Success - refresh credits and subscription plan
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['subscriptionPlan'] }),
          queryClient.refetchQueries({ queryKey: ['creditProducts', 'stripe'] }),
        ]);

        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: `Credits purchased successfully! ${product.metadata?.credits_amount || ''} credits have been added to your account.`,
            type: 'success'
          },
        });
      }
    } catch (error: any) {
      console.error('❌ [AI Credits] Purchase error:', {
        error: error.message,
        stack: error.stack,
        productId: product.id,
      });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: error.message || 'Failed to process purchase. Please try again.',
          type: 'error',
        },
      });
    } finally {
      dispatch({ type: HIDE_LOADING_MODAL });
      setPaymentInProgress(false);
    }
  }, [user, dispatch, queryClient, initPaymentSheet, presentPaymentSheet]);

  // Handle IAP purchase
  const handleIAPPurchase = useCallback(async (product: any) => {
    if (!iapContext?.connected || !iapContext?.requestPurchase) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Store not connected. Please try again.', type: 'warning' },
      });
      return;
    }

    try {
      setPaymentInProgress(true);
      dispatch({ type: SHOW_LOADING_MODAL });

      console.log('🛒 [AI Credits IAP] Starting IAP purchase:', {
        productId: product.productId || product.id,
        productName: product.name || product.title,
        platform: Platform.OS,
      });

      await iapContext.requestPurchase({
        request: {
          ios: { sku: product.productId || product.id },
          android: { skus: [product.productId || product.id] },
        },
        type: 'purchase', // One-time purchase, not subscription
      });

      console.log('✅ [AI Credits IAP] Purchase request sent, waiting for IAPContext to handle...');

      // Success will be handled by IAPContext
      // Refetch queries after a short delay to allow backend processing
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ['subscriptionPlan'] });
        queryClient.refetchQueries({ queryKey: ['creditProducts', 'stripe'] });
      }, 2000);
    } catch (error: any) {
      console.error('❌ [AI Credits IAP] Purchase error:', {
        error: error.message,
        stack: error.stack,
        productId: product.productId || product.id,
      });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: error.message || 'Failed to process purchase. Please try again.',
          type: 'error',
        },
      });
    } finally {
      dispatch({ type: HIDE_LOADING_MODAL });
      setPaymentInProgress(false);
    }
  }, [iapContext, dispatch, queryClient]);

  const handlePurchase = useCallback((product: any) => {
    if (isNative && activeTab === 'iap') {
      handleIAPPurchase(product);
    } else {
      handleStripePurchase(product);
    }
  }, [isNative, activeTab, handleIAPPurchase, handleStripePurchase]);

  // Determine which products to show
  const displayProducts = isNative && activeTab === 'iap' ? iapCreditProducts : creditProducts;
  const isLoading = isNative && activeTab === 'iap' ? !iapContext?.connected : creditsLoading;

  const RenderCreditCard = ({ item, index }: { item: any; index: number }) => {
    const isSelected = selectedCredit?.id === item.id;
    const price = item.default_price
      ? formatPrice(item.default_price.unit_amount, item.default_price.currency)
      : item.localizedPrice || item.price || 'N/A';

    return (
      <Animatable.View
        animation="fadeInUp"
        delay={index * 100}
        style={{ marginBottom: 16 }}
      >
        <LinearGradient
          colors={theme ? ['#667eea', '#764ba2'] : ['#4a90e2', '#357abd']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{
            borderRadius: 16,
            padding: 3,
            shadowColor: theme ? '#667eea' : '#4a90e2',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 8,
          }}
        >
          <Pressable
            onPress={() => setSelectedCredit(isSelected ? null : item)}
            style={{
              backgroundColor: theme ? '#FFFFFF' : '#1F2937',
              borderRadius: 13,
              padding: 20,
              borderWidth: isSelected ? 2 : 0,
              borderColor: '#3b82f6',
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
              <View style={{
                backgroundColor: theme ? '#ddd6fe' : '#312e81',
                borderRadius: 12,
                padding: 12,
                marginRight: 12,
              }}>
                <MaterialIcons
                  name="auto-awesome"
                  size={24}
                  color={theme ? '#7c3aed' : '#a78bfa'}
                />
              </View>
              <View style={{ flex: 1 }}>
                <H6 style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontWeight: '700',
                  marginBottom: 4,
                }}>
                  {item.name || item.title}
                </H6>
                {(item.metadata?.credits_amount || item.metadata?.credit_amount) && (
                  <Text style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 14,
                    fontWeight: '600',
                  }}>
                    {item.metadata.credits_amount || item.metadata.credit_amount} AI Credits
                  </Text>
                )}
                {item.id === AI_CREDITS_PRODUCT_ID && !item.metadata?.credits_amount && (
                  <Text style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 14,
                    fontWeight: '600',
                  }}>
                    AI Generation Credits
                  </Text>
                )}
              </View>
            </View>

            {item.description && (
              <Text style={{
                color: theme ? '#64748b' : '#94a3b8',
                fontSize: 14,
                marginBottom: 16,
                lineHeight: 20,
              }}>
                {item.description}
              </Text>
            )}

            <View style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 12,
            }}>
              <View>
                <Text style={{
                  color: theme ? '#1e293b' : '#f1f5f9',
                  fontSize: 24,
                  fontWeight: '700',
                }}>
                  {price}
                </Text>
                {item.default_price && (
                  <Text style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 12,
                  }}>
                    One-time purchase
                  </Text>
                )}
              </View>

              <Pressable
                onPress={() => handlePurchase(item)}
                disabled={paymentInProgress}
                style={{
                  backgroundColor: isSelected ? '#3b82f6' : (theme ? '#f1f5f9' : '#334155'),
                  paddingHorizontal: 20,
                  paddingVertical: 10,
                  borderRadius: 8,
                  opacity: paymentInProgress ? 0.6 : 1,
                }}
              >
                <Text style={{
                  color: isSelected ? 'white' : (theme ? '#1e293b' : '#f1f5f9'),
                  fontWeight: '600',
                  fontSize: 14,
                }}>
                  {paymentInProgress ? 'Processing...' : 'Purchase'}
                </Text>
              </Pressable>
            </View>
          </Pressable>
        </LinearGradient>
      </Animatable.View>
    );
  };

  if (isLoading) {
    return (
      <View style={{ padding: 40, alignItems: 'center' }}>
        <Spinner size="large" color={theme ? '#3b82f6' : '#60a5fa'} />
        <Text style={{
          color: theme ? '#64748b' : '#94a3b8',
          marginTop: 16,
        }}>
          Loading credit packages...
        </Text>
      </View>
    );
  }

  // Show unlimited credits if user has active subscription or EXEMPT_PRODUCT
  if (hasActiveSubscription || isExemptProduct) {
    return (
      <View style={{
        padding: 40,
        alignItems: 'center',
        backgroundColor: theme ? '#f0fdf4' : '#064e3b',
        borderRadius: 12,
        marginTop: 16,
        borderWidth: 2,
        borderColor: theme ? '#22c55e' : '#10b981',
      }}>
        <LinearGradient
          colors={theme ? ['#22c55e', '#16a34a'] : ['#10b981', '#059669']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{
            width: 80,
            height: 80,
            borderRadius: 40,
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 20,
          }}
        >
          <MaterialIcons
            name="all-inclusive"
            size={40}
            color="white"
          />
        </LinearGradient>
        <H6 style={{
          color: theme ? '#1e293b' : '#f1f5f9',
          fontWeight: '700',
          fontSize: 24,
          marginBottom: 12,
          textAlign: 'center',
        }}>
          Unlimited Credits
        </H6>
        <Text style={{
          color: theme ? '#64748b' : '#94a3b8',
          fontSize: 16,
          textAlign: 'center',
          lineHeight: 24,
          marginBottom: 8,
        }}>
          {isExemptProduct
            ? 'You have unlimited AI generation credits with your exempt plan.'
            : 'Your active subscription includes unlimited AI generation credits.'}
        </Text>
        <Text style={{
          color: theme ? '#059669' : '#10b981',
          fontSize: 14,
          textAlign: 'center',
          fontWeight: '600',
        }}>
          No additional purchases needed!
        </Text>
        {currentPlan?.product?.name && (
          <View style={{
            marginTop: 20,
            paddingHorizontal: 16,
            paddingVertical: 8,
            backgroundColor: theme ? '#dcfce7' : '#065f46',
            borderRadius: 8,
          }}>
            <Text style={{
              color: theme ? '#166534' : '#86efac',
              fontSize: 12,
              textAlign: 'center',
            }}>
              Active Plan: {currentPlan.product.name}
            </Text>
          </View>
        )}
      </View>
    );
  }

  // Show usage stats for free plan users (before credit products)
  const currentUsage = aiUsageStats?.currentCount || aiUsageStats?.thisMonth || 0;
  const usageLimit = aiUsageStats?.limit || 5; // Default to 5 for free plan
  const usagePercentage = usageLimit > 0 ? Math.min(Math.round((currentUsage / usageLimit) * 100), 100) : 0;

  return (
    <ScrollView
      style={{ flex: 1 }}
      showsVerticalScrollIndicator={false}
    >
      <YStack gap={16}>
        {/* Show usage stats for free plan users (but not for EXEMPT_PRODUCT) */}
        {isFreePlan && !isExemptProduct && (
          <View style={{
            paddingVertical: 24,
            backgroundColor: theme ? '#fef3c7' : '#78350f',
            borderRadius: 12,
            borderWidth: 2,
            padding: 15,
            borderColor: theme ? '#f59e0b' : '#fbbf24',
          }}>
            <View style={{ flexDirection: 'row', gap: 15, alignItems: 'center', marginBottom: 16 }}>
              <View style={{
                backgroundColor: theme ? '#fbbf24' : '#f59e0b',
                borderRadius: 8,
                padding: 8,
              }}>
                <MaterialIcons
                  name="auto-awesome"
                  size={20}
                  color="white"
                />
              </View>
              <H6 style={{
                color: theme ? '#1e293b' : '#f1f5f9',
                fontWeight: '600',
              }}>
                AI Generation Usage
              </H6>
            </View>
            {aiUsageLoading ? (
              <View style={{ alignItems: 'center', paddingVertical: 20 }}>
                <Spinner size="small" color={theme ? '#f59e0b' : '#fbbf24'} />
                <Text style={{
                  color: theme ? '#64748b' : '#94a3b8',
                  marginTop: 12,
                  fontSize: 14,
                }}>
                  Loading usage data...
                </Text>
              </View>
            ) : (
              <>
                <View style={{
                  width: '100%',
                  marginBottom: 12,
                }}>
                  <View style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}>
                    <Text style={{
                      color: theme ? '#1e293b' : '#f1f5f9',
                      fontSize: 20,
                      fontWeight: '700',
                    }}>
                      {currentUsage} / {usageLimit}
                    </Text>
                    <Text style={{
                      color: theme ? '#64748b' : '#94a3b8',
                      fontSize: 14,
                      fontWeight: '600',
                    }}>
                      {usagePercentage}% used
                    </Text>
                  </View>
                  <View style={{
                    width: '100%',
                    height: 10,
                    backgroundColor: theme ? '#fef3c7' : '#92400e',
                    borderRadius: 5,
                    overflow: 'hidden',
                  }}>
                    <View style={{
                      width: `${usagePercentage}%`,
                      height: '100%',
                      backgroundColor: theme ? '#f59e0b' : '#fbbf24',
                      borderRadius: 5,
                    }} />
                  </View>
                </View>
                <Text style={{
                  color: theme ? '#64748b' : '#94a3b8',
                  fontSize: 14,
                  textAlign: 'center',
                  lineHeight: 20,
                  marginBottom: 8,
                }}>
                  You've used {currentUsage} of {usageLimit} AI generations this month.
                </Text>
                <Text style={{
                  color: theme ? '#92400e' : '#fbbf24',
                  fontSize: 12,
                  textAlign: 'center',
                  fontWeight: '600',
                }}>
                  Upgrade to unlock unlimited AI generations!
                </Text>
              </>
            )}
          </View>
        )}

        {/* Show credit products */}
        {displayProducts.length === 0 ? (
          <View style={{
            padding: 40,
            alignItems: 'center',
            backgroundColor: theme ? '#f8fafc' : '#0f172a',
            borderRadius: 12,
            marginTop: 16,
          }}>
            <MaterialIcons
              name="info-outline"
              size={48}
              color={theme ? '#64748b' : '#94a3b8'}
            />
            <Text style={{
              color: theme ? '#64748b' : '#94a3b8',
              marginTop: 16,
              textAlign: 'center',
            }}>
              No credit packages available at this time.
            </Text>
          </View>
        ) : (
          <>
            {displayProducts.map((product, index) => (
              <RenderCreditCard key={product.id || product.productId || index} item={product} index={index} />
            ))}
            
            {/* Terms and Conditions */}
            <View style={{
              backgroundColor: theme ? '#fef3c7' : '#78350f',
              borderRadius: 12,
              padding: 16,
              borderWidth: 1,
              borderColor: theme ? '#f59e0b' : '#fbbf24',
              marginTop: 8,
            }}>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8 }}>
                <MaterialIcons
                  name="info-outline"
                  size={18}
                  color={theme ? '#92400e' : '#fbbf24'}
                  style={{ marginRight: 8, marginTop: 2 }}
                />
                <YStack flex={1}>
                  <Text style={{
                    fontSize: 13,
                    fontWeight: '600',
                    color: theme ? '#92400e' : '#fbbf24',
                    marginBottom: 6,
                  }}>
                    Terms & Conditions
                  </Text>
                  <Text style={{
                    fontSize: 12,
                    color: theme ? '#78350f' : '#fcd34d',
                    lineHeight: 18,
                  }}>
                    By purchasing AI credits, you consent to the immediate provision of digital content and acknowledge that this waives your right to cancel under the Consumer Contracts Regulations 2013. Credits are non-refundable once purchased. Credits never expire and can be used across all AI features. Your statutory rights are unaffected - if credits are faulty or not as described, contact admin@smartsaas.co.uk for assistance.
                  </Text>
                </YStack>
              </View>
            </View>
          </>
        )}
      </YStack>
    </ScrollView>
  );
};

export default AICreditsComponent;

